import {
	ExactUrlPattern,
	UrlMatcher,
	UrlPatternType,
	DomainUrlPattern,
	HostnameUrlPattern,
	OriginUrlPattern,
	WildcardUrlPattern,
	RegexUrlPattern,
	CompositeUrlPattern,
	UrlPattern,
} from "./types.js";

const ExactUrlMatcher = {
	create(urlPattern: ExactUrlPattern): UrlMatcher<UrlPatternType.EXACT> {
		const urlStr = urlPattern.url.toString();
		return {
			type: UrlPatternType.EXACT,
			match(url: URL | string) {
				return urlStr === url.toString();
			},
		};
	},
};

const DomainUrlMatcher = {
	extractRootDomain(url: string | URL) {
		try {
			let domain = new URL(url).hostname;
			const splitArr = domain.split(".");
			const arrLen = splitArr.length;

			//extracting the root domain here
			//if there is a subdomain
			if (arrLen > 2) {
				domain = splitArr[arrLen - 2] + "." + splitArr[arrLen - 1];
				//check to see if it's using a Country Code Top Level Domain (ccTLD) (i.e. ".me.uk")
				if (splitArr[arrLen - 2].length == 2 && splitArr[arrLen - 1].length == 2) {
					//this is using a ccTLD
					domain = splitArr[arrLen - 3] + "." + domain;
				}
			}
			return domain;
		} catch (err) {
			console.error(err);
		}
	},
	create(urlPattern: DomainUrlPattern): UrlMatcher<UrlPatternType.DOMAIN> {
		const domain = urlPattern.domain ?? DomainUrlMatcher.extractRootDomain(urlPattern.url.toString());
		if (!domain) throw new Error("could not get domain");
		return {
			type: UrlPatternType.DOMAIN,
			match(url: URL | string) {
				const urlDomain = DomainUrlMatcher.extractRootDomain(url);
				return urlDomain !== undefined && urlDomain === domain;
			},
		};
	},
};

const HostnameUrlMatcher = {
	create(urlPattern: HostnameUrlPattern): UrlMatcher<UrlPatternType.HOSTNAME> {
		try {
			const hostname = urlPattern.hostname ?? new URL(urlPattern.url).hostname;
			return {
				type: UrlPatternType.HOSTNAME,
				match(url: URL | string) {
					try {
						return hostname === new URL(url).hostname;
					} catch (err) {
						return false;
					}
				},
			};
		} catch (err) {
			throw new Error(`could not get hostname`);
		}
	},
};

const OriginUrlMatcher = {
	create(urlPattern: OriginUrlPattern): UrlMatcher<UrlPatternType.ORIGIN> {
		try {
			const origin = urlPattern.origin ?? new URL(urlPattern.url).origin;
			return {
				type: UrlPatternType.ORIGIN,
				match(url: URL | string) {
					try {
						return origin === new URL(url).origin;
					} catch (err) {
						return false;
					}
				},
			};
		} catch (err) {
			throw new Error(`could not get origin`);
		}
	},
};

const WildcardUrlMatcher = {
	create(urlPattern: WildcardUrlPattern): UrlMatcher<UrlPatternType.WILDCARD> {
		const wildcardPattern = urlPattern.pattern;
		return {
			type: UrlPatternType.WILDCARD,
			match(url: URL | string) {
				const urlStr = url.toString();
				const matchLookup = Array.from(new Array<boolean[]>(wildcardPattern.length + 1), (_) =>
					new Array<boolean>(urlStr.length + 1).fill(false),
				);
				matchLookup[0][0] = true;
				for (let w = 0; w < wildcardPattern.length; w++) {
					if (wildcardPattern[w] !== "*") {
						break;
					}
					matchLookup[w + 1][0] = true;
				}
				for (let w = 0; w < wildcardPattern.length; w++) {
					for (let u = 0; u < urlStr.length; u++) {
						if (wildcardPattern[w] === "*") {
							matchLookup[w + 1][u + 1] = matchLookup[w + 1][u] || matchLookup[w][u + 1];
						} else if (wildcardPattern[w] === "?" || wildcardPattern[w] === urlStr[u]) {
							matchLookup[w + 1][u + 1] = matchLookup[w][u];
						} else if (wildcardPattern[w] !== urlStr[u]) {
							matchLookup[w + 1][u + 1] = false;
						}
					}
				}
				return matchLookup[wildcardPattern.length][urlStr.length];
			},
		};
	},
};

const RegexUrlMatcher = {
	create(urlPattern: RegexUrlPattern): UrlMatcher<UrlPatternType.REGEX> {
		let regexStr = urlPattern.regex.toString();
		if (!regexStr.startsWith("^")) {
			regexStr = `^${regexStr}`;
		}
		if (!regexStr.endsWith("$")) {
			regexStr = `${regexStr}$`;
		}
		const regex = new RegExp(regexStr);
		return {
			type: UrlPatternType.REGEX,
			match(url: URL | string) {
				try {
					return regex.test(url.toString());
				} catch (err) {
					return false;
				}
			},
		};
	},
};

const CompositeUrlMatcher = {
	create(urlPattern: CompositeUrlPattern): UrlMatcher<UrlPatternType.COMPOSITE> {
		const urlMatchers = urlPattern.urlPatterns.map((u) => createUrlMatcher(u));
		return {
			type: UrlPatternType.COMPOSITE,
			match(url: URL | string) {
				return urlMatchers.some((m) => m.match(url));
			},
		};
	},
};

// TO DO USE LOCKDOWN's url matching?
export function createUrlMatcher<P extends UrlPattern>(urlPattern: P): UrlMatcher<P["type"]> {
	switch (urlPattern.type) {
		case UrlPatternType.EXACT: {
			return ExactUrlMatcher.create(urlPattern);
		}
		case UrlPatternType.DOMAIN: {
			return DomainUrlMatcher.create(urlPattern);
		}
		case UrlPatternType.HOSTNAME: {
			return HostnameUrlMatcher.create(urlPattern);
		}
		case UrlPatternType.ORIGIN: {
			return OriginUrlMatcher.create(urlPattern);
		}
		case UrlPatternType.WILDCARD: {
			return WildcardUrlMatcher.create(urlPattern);
		}
		case UrlPatternType.REGEX: {
			return RegexUrlMatcher.create(urlPattern);
		}
		case UrlPatternType.COMPOSITE: {
			return CompositeUrlMatcher.create(urlPattern);
		}
	}
}
